/**
 * LatestAlertsTable — the "Latest alerts" widget below the summary charts.
 *
 * SkyWatch improves on Dedrone here: rows are clickable (Dedrone's are
 * `cursor: default`) and open a slide-in detail drawer. We keep the columns
 * tight — drone, serial, zone, severity, duration, timestamp — because the
 * drawer is where the full record lives.
 */
import { formatDistanceToNow, format } from 'date-fns';
import clsx from 'clsx';
import type { LatestAlertRow, Severity } from '../../services/reportsApi';

function severityClass(sev: Severity): string {
  switch (sev) {
    case 'CRITICAL':
      return 'bg-rose-500/10 text-rose-400 border border-rose-500/30';
    case 'WARNING':
      return 'bg-amber-500/10 text-amber-400 border border-amber-500/30';
    case 'INFO':
    default:
      return 'bg-sky-500/10 text-sky-400 border border-sky-500/30';
  }
}

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

interface Props {
  rows: LatestAlertRow[];
  onRowClick: (alertId: string) => void;
  title?: string;
}

export default function LatestAlertsTable({
  rows,
  onRowClick,
  title = 'Latest Detections',
}: Props) {
  return (
    <div className="glass-card p-0 overflow-hidden">
      <div className="flex items-center justify-between p-5 pb-3 border-b border-navy-700">
        <div>
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <p className="text-xs text-gray-500">Click any row for full detail</p>
        </div>
        <span className="text-xs text-gray-500">{rows.length.toLocaleString()}</span>
      </div>

      {rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-500">
          No detections in the selected range.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 uppercase tracking-wider">
              <tr className="border-b border-navy-700">
                <th className="px-4 py-2 font-medium">Severity</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Drone</th>
                <th className="px-4 py-2 font-medium">Serial</th>
                <th className="px-4 py-2 font-medium">Zone</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => onRowClick(r.id)}
                  className={clsx(
                    'border-b border-navy-800 hover:bg-navy-800/40 cursor-pointer transition-colors',
                    r.acknowledged && 'opacity-60',
                  )}
                >
                  <td className="px-4 py-2.5">
                    <span
                      className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold',
                        severityClass(r.severity),
                      )}
                    >
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">
                    {r.type.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-2.5 text-white whitespace-nowrap">
                    {r.droneModel || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-300 font-mono text-xs whitespace-nowrap">
                    {r.serialNumber ? r.serialNumber.slice(-8) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">
                    {r.zoneName || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-300 font-mono text-xs whitespace-nowrap">
                    {formatDuration(r.durationSec)}
                  </td>
                  <td
                    className="px-4 py-2.5 text-gray-400 whitespace-nowrap"
                    title={format(new Date(r.createdAt), 'yyyy-MM-dd HH:mm:ss')}
                  >
                    {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
